import { useEffect, useState } from 'react';
import { db } from '@/lib/db';
import type { GroupRoundEnriched, Member, MemberPublic } from '@/lib/db/types';
import { OrganizerActions } from '@/components/shared/OrganizerActions';
import { AlertDialog } from '@/components/shared/AlertDialog';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Sheet } from '@/components/shared/Sheet';

interface Props {
  round: GroupRoundEnriched;
  member: Member;
  onClose: () => void;
  onUpdated: () => void;
  onCancelled: () => void;
}

type PendingAction = 'cancel' | 'delete' | null;

export function RoundSettingsSheet({ round, member, onClose, onUpdated, onCancelled }: Props) {
  const [members, setMembers] = useState<MemberPublic[]>([]);
  const [transferTo, setTransferTo] = useState('');
  const [transferBusy, setTransferBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const isOrganizer = round.organizer_id === member.id;

  useEffect(() => {
    const playerMemberIds = new Set(
      (round.players || []).map((p) => p.member_id).filter(Boolean) as string[],
    );
    void db.members.list({ limit: 100 }).then((all) => {
      setMembers(all.filter((m) => playerMemberIds.has(m.id) && m.id !== member.id));
    });
  }, [round.players, member.id]);

  const handleTransfer = async () => {
    if (!transferTo) return;
    setTransferBusy(true);
    try {
      await db.groupRounds.transferOrganizer(round.id, transferTo);
      onUpdated();
      onClose();
    } finally {
      setTransferBusy(false);
    }
  };

  const confirmCancel = async () => {
    setDeleteBusy(true);
    setActionError(null);
    try {
      await db.groupRounds.cancel(round.id);
      setPendingAction(null);
      onCancelled();
    } catch (err) {
      setActionError((err as Error).message || 'Could not cancel round');
      setPendingAction(null);
    } finally {
      setDeleteBusy(false);
    }
  };

  const confirmDelete = async () => {
    setDeleteBusy(true);
    setActionError(null);
    try {
      await db.groupRounds.delete(round.id);
      setPendingAction(null);
      onCancelled();
    } catch (err) {
      setActionError((err as Error).message || 'Could not delete round');
      setPendingAction(null);
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <>
      <Sheet title="Round Settings" onClose={onClose}>
        <div className="space-y-4 px-4 pb-8 pt-2">
          {!isOrganizer ? (
            <p className="text-sm text-ink-soft">Only the organizer can change round settings.</p>
          ) : (
            <>
              <OrganizerActions
                members={members}
                currentMemberId={member.id}
                transferTo={transferTo}
                onTransferToChange={setTransferTo}
                onTransfer={() => void handleTransfer()}
                onDelete={() => setPendingAction('cancel')}
                deleteLabel="Cancel Round"
                transferBusy={transferBusy}
                deleteBusy={deleteBusy}
              />
              <button
                type="button"
                disabled={deleteBusy}
                onClick={() => setPendingAction('delete')}
                className="w-full rounded-full border border-bad/30 bg-bad/5 py-2.5 text-sm font-bold text-bad disabled:opacity-50"
              >
                {deleteBusy ? 'Deleting…' : 'Delete Round Permanently'}
              </button>
            </>
          )}
        </div>
      </Sheet>

      <ConfirmDialog
        open={pendingAction === 'cancel'}
        title="Cancel this round?"
        message="Scores will be kept but the round will end."
        confirmLabel="Cancel Round"
        destructive
        busy={deleteBusy}
        onConfirm={() => void confirmCancel()}
        onCancel={() => setPendingAction(null)}
      />
      <ConfirmDialog
        open={pendingAction === 'delete'}
        title="Delete this round permanently?"
        message="This cannot be undone."
        confirmLabel="Delete"
        destructive
        busy={deleteBusy}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setPendingAction(null)}
      />
      <AlertDialog
        open={actionError != null}
        title="Something went wrong"
        message={actionError}
        onClose={() => setActionError(null)}
      />
    </>
  );
}
